import React, { FC } from 'react';

import { Entry } from 'contentful';

import * as allComponents from '@/components';

import { ComponentType } from '@/core/types';

import { capitalizeFirstLetter } from '@/utils/helpers';

import { shouldHideWithLocaleOverride } from '@/utils/localeOverride';
import { devConsoleLog } from '@/utils/logging';

export interface DynamicComponentProps {
  component: Entry<Record<string, any>> | undefined;
  [prop: string]: any;
}

const DynamicComponent: FC<DynamicComponentProps> = ({
  component,
  ...props
}) => {
  if (!component) {
    return null;
  }

  const { fields, sys } = component;

  const contentEntryId = sys && sys.id;

  // Missing fields occur when a linked Contentful entry is missing in a draft state
  if (!fields) {
    devConsoleLog(
      `No field found on content entry: ${contentEntryId}. The content entry might be missing or inaccessible.`,
    );

    return null;
  }

  if (shouldHideWithLocaleOverride(fields.localeOverride)) {
    return null;
  }

  // Workaround for Contentful's inconsistent content type naming convention.
  const contentfulContentType =
    sys &&
    (sys.contentType.sys.id === 'link_external'
      ? 'ExternalLink'
      : sys.contentType.sys.id);

  const systemContentType = capitalizeFirstLetter(contentfulContentType);

  const componentType = fields.componentName || systemContentType;

  const Component = allComponents[componentType as ComponentType];
  if (!Component) {
    devConsoleLog(
      `Unrecognized dynamic component content type: ${componentType}`,
      {
        component,
      },
    );

    return null;
  }

  if (contentfulContentType === 'ExternalLink') {
    return <Component link={{ ...component }} {...props} />;
  }

  return <Component {...fields} {...props} />;
};

export default React.memo(DynamicComponent);
